import {
    Box,
    CircularProgress,
    Paper,
    TableContainer,
    Typography,
    Tab,
    Tabs,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import DateRangeDisplay from 'app/components/dateRange/dateRangeDisplay/dateRangeDisplay'
import { SearchContainer, SearchInput } from 'app/components/search/Search'
import { dashboardService } from 'core/api/dashboard/dashboardService'
import theme from 'core/theme/theme'
import { formatCurrencyBR, formatDateToSearch } from 'core/utils/globalFunctions'
import dayjs, { Dayjs } from 'dayjs'
import { useState, useMemo } from 'react'
import Chart from 'react-google-charts'
import styled from 'styled-components'
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';

// Definição dos tipos de dados
interface Product {
    productName: string;
    totalAmount: number;
    priceUnitCost: number;
}

interface CategoryData {
    categoryName: string;
    products: Product[];
}

// Função para formatar o nome do produto com a primeira letra maiúscula
const formatProductName = (name: string): string => {
    return name
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

export default function CategoryProductsDashboard() {
    // Estado do intervalo de data
    const [initDate, setInitDate] = useState<Dayjs | null | undefined>(
        dayjs().startOf('month')
    )
    const [finalDate, setFinalDate] = useState<Dayjs | null | undefined>(
        dayjs().endOf('month')
    )

    // Estado para controlar a categoria selecionada
    const [selectedCategory, setSelectedCategory] = useState<number>(0);

    // Estado para controlar a visualização (gráfico ou tabela)
    // Alterado para 'table' por padrão
    const [view, setView] = useState<'chart' | 'table'>('table');

    // Estado para controle de visualização
    const [displayMode, setDisplayMode] = useState<'top10' | 'top25' | 'top50' | 'all'>('all');

    // Estados para paginação na tabela
    const [page, setPage] = useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = useState<number>(1000);

    // Estado para pesquisa de produtos
    const [searchTerm, setSearchTerm] = useState<string>('');

    // Estado para filtro de quantidade
    const [quantityFilter, setQuantityFilter] = useState<string>('all');

    // Busca os dados usando React Query
    const categoryDataQuery = useQuery({
        queryKey: ['itemsByCategory', initDate, finalDate],
        staleTime: Infinity,
        queryFn: () => {
            // Verificar se as datas inicial e final são do mesmo dia
            const isSameDay = initDate?.isSame(finalDate, 'day');

            // Adiciona 1 dia à data final APENAS se for o mesmo dia
            const adjustedFinalDate = isSameDay ? finalDate?.add(1, 'day') : finalDate;

            return dashboardService.getItemsByCategory(
                formatDateToSearch(initDate!),
                formatDateToSearch(adjustedFinalDate!)
            );
        }
    });

    const categoryData: CategoryData[] = categoryDataQuery.data || [];

    // Função para lidar com a mudança de categoria
    const handleCategoryChange = (event: React.SyntheticEvent, newValue: number) => {
        setSelectedCategory(newValue);
        // Limpar o termo de pesquisa ao trocar de categoria
        setSearchTerm('');
        // Redefinir a página ao mudar de categoria
        setPage(0);
    };

    // Função para alternar entre visualização de gráfico e tabela
    const toggleView = () => {
        setView(prev => prev === 'chart' ? 'table' : 'chart');
        // Limpar pesquisa ao mudar para gráfico (opcional)
        if (view === 'table') {
            setSearchTerm('');
        }
    };

    // Função para alterar o modo de visualização
    const handleDisplayModeChange = (mode: 'top10' | 'top25' | 'top50' | 'all') => {
        setDisplayMode(mode);
        setPage(0); // Resetar para a primeira página quando mudar o modo
    };

    // Função para lidar com a pesquisa
    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
        setPage(0); // Resetar para a primeira página quando pesquisar
    };

    // Preparar dados para o gráfico e tabela
    const currentCategoryData = useMemo(() => {
        if (!categoryData.length || selectedCategory >= categoryData.length) {
            return null;
        }

        // Obter produtos da categoria e ordenar por quantidade
        let filteredProducts = [...categoryData[selectedCategory].products];

        // Aplicar filtro de pesquisa se houver e estiver em modo tabela
        if (searchTerm && view === 'table') {
            const term = searchTerm.toLowerCase();
            filteredProducts = filteredProducts.filter(product =>
                formatProductName(product.productName).toLowerCase().includes(term)
            );
        }

        // Aplicar filtro de quantidade
        if (quantityFilter !== 'all') {
            const minValue = parseInt(quantityFilter.split('-')[0], 10);
            const maxValue = quantityFilter.includes('+')
                ? Infinity
                : parseInt(quantityFilter.split('-')[1], 10);

            filteredProducts = filteredProducts.filter(product =>
                product.totalAmount >= minValue && product.totalAmount <= maxValue
            );
        }

        // Ordenar por quantidade (maior para menor)
        const sortedProducts = filteredProducts.sort((a, b) => b.totalAmount - a.totalAmount);

        // Total de produtos após filtros para informação ao usuário
        const totalFilteredCount = sortedProducts.length;

        // Calcular a soma total das quantidades vendidas
        const totalQuantitySold = sortedProducts.reduce((sum, product) => sum + product.totalAmount, 0);

        const totalAmountSold = sortedProducts.reduce((sum, product) => sum + product.totalAmount * product.priceUnitCost, 0);

        // Aplicar filtro de top N para visualização de tabela baseado no displayMode
        let displayProducts = [...sortedProducts];

        if (displayMode !== 'all') {
            const limit = displayMode === 'top10' ? 10 : displayMode === 'top25' ? 25 : 50;
            displayProducts = sortedProducts.slice(0, limit);
        }

        // Limitar para o modo de visualização do gráfico
        let limitedForChart;
        switch (displayMode) {
            case 'top10':
                limitedForChart = sortedProducts.slice(0, 10);
                break;
            case 'top25':
                limitedForChart = sortedProducts.slice(0, 25);
                break;
            case 'top50':
                limitedForChart = sortedProducts.slice(0, 50);
                break;
            default:
                limitedForChart = sortedProducts.slice(0, 100); // Para 'all', limitar a 100 para performance no gráfico
        }

        return {
            totalAmountSold: totalAmountSold,
            categoryName: categoryData[selectedCategory].categoryName,
            products: displayProducts, // Produtos filtrados pelo displayMode
            allProducts: sortedProducts, // Todos os produtos filtrados
            productsForChart: limitedForChart,
            totalFilteredCount,
            totalQuantitySold
        };
    }, [categoryData, selectedCategory, displayMode, searchTerm, quantityFilter, view]);

    // Função para remover o nome da categoria do início do nome do produto no gráfico
    const removeCategNameFromProduct = (categoryName: string, productName: string): string => {
        // Verifica se o nome da categoria está no início do nome do produto
        const categoryWords = categoryName.split(' ');
        let processedName = productName;

        // Tenta remover cada palavra da categoria do início do nome do produto
        for (const word of categoryWords) {
            if (word.length > 2) { // Ignora palavras muito curtas
                const regex = new RegExp(`^${word}\\s+`, 'i');
                processedName = processedName.replace(regex, '');
            }
        }

        return formatProductName(processedName);
    };

    // Preparar dados para o gráfico de barras
    const barChartData = useMemo(() => {
        if (!currentCategoryData) {
            return [];
        }

        return [
            ['Produto', 'Quantidade', { role: 'tooltip', type: 'string', p: { html: true } }],
            ...currentCategoryData.productsForChart.map(product => {


                // Versão mais curta para exibição na barra (apenas 15 caracteres)
                const displayName = product.productName.length > 15
                    ? `${product.productName.substring(0, 15)}...`
                    : product.productName;

                // Nome completo para o tooltip
                const fullName = formatProductName(product.productName);

                // Tooltip personalizado com o nome completo do produto
                const tooltip = `${product.productName}
                    Quantidade: ${product.totalAmount}`;

                return [displayName, product.totalAmount, tooltip];
            })
        ];
    }, [currentCategoryData]);

    // Renderização dos controles de filtro que sempre ficam visíveis
    const renderControls = () => (
        <>
            {/* Área de filtros e pesquisa */}
            <FilterBox>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    {/* Desabilita a pesquisa quando estiver em modo gráfico */}
                    {view === 'table' && categoryData.length > 0 ? (
                        <SearchContainer>
                            <SearchIcon
                                sx={{
                                    color: theme.COLORS.YELLOW2,
                                    width: 20,
                                    height: 20,
                                }}
                            />
                            <SearchInput
                                placeholder="Pesquisar por nome..."
                                value={searchTerm}
                                onChange={handleSearch}
                            />
                            <CloseIcon
                                onClick={() => { setSearchTerm(""); setPage(0) }}
                                sx={{
                                    color: theme.COLORS.YELLOW2,
                                    width: 20,
                                    height: 20,
                                    cursor: 'pointer',
                                    ":hover": {
                                        color: theme.COLORS.YELLOW
                                    }
                                }}
                            />
                        </SearchContainer>
                    ) : null}
                </Box>

                <StyledBox>
                    <StyledTypography>Período: </StyledTypography>
                    <DateRangeDisplay
                        typeOfDatePicker={'day'}
                        initialDate={initDate}
                        endDate={finalDate}
                        setInitialDate={setInitDate}
                        setFinalDate={setFinalDate}
                    />
                </StyledBox>
            </FilterBox>

            {/* Área de controle */}
            {categoryData.length > 0 && (
                <ControlBox>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <DisplaySelector>
                            <DisplayLabel>Exibição:</DisplayLabel>
                            <ActionButton
                                onClick={() => handleDisplayModeChange('top10')}
                                className={displayMode === 'top10' ? 'active' : ''}
                            >
                                Top 10
                            </ActionButton>
                            <ActionButton
                                onClick={() => handleDisplayModeChange('top25')}
                                className={displayMode === 'top25' ? 'active' : ''}
                            >
                                Top 25
                            </ActionButton>
                            <ActionButton
                                onClick={() => handleDisplayModeChange('top50')}
                                className={displayMode === 'top50' ? 'active' : ''}
                            >
                                Top 50
                            </ActionButton>
                            <ActionButton
                                onClick={() => handleDisplayModeChange('all')}
                                className={displayMode === 'all' ? 'active' : ''}
                            >
                                Todos
                            </ActionButton>
                        </DisplaySelector>
                    </Box>

                    <ActionButton
                        onClick={toggleView}
                        disabled={!currentCategoryData}
                    >
                        {view === 'chart' ? 'Ver Tabela' : 'Ver Gráficos'}
                    </ActionButton>
                </ControlBox>
            )}
        </>
    );

    return (
        <TableContainer
            sx={{ overflow: 'hidden', padding: 1, height: '100%' }}
            component={Paper}
        >
            {/* Tabs para seleção de categoria sempre visível quando há dados */}
            {categoryData.length > 0 && (
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs
                        value={selectedCategory}
                        onChange={handleCategoryChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                            '& .MuiTabs-indicator': {
                                backgroundColor: theme.COLORS.YELLOW2
                            },
                            '& .Mui-selected': {
                                color: `${theme.COLORS.YELLOW2} !important`
                            }
                        }}
                    >
                        {categoryData.map((category, index) => (
                            <Tab
                                key={index}
                                label={category.categoryName}
                                sx={{
                                    fontWeight: 'bold',
                                    color: theme.COLORS.GRAY3
                                }}
                            />
                        ))}
                    </Tabs>
                </Box>
            )}

            {/* Controles de filtro sempre visíveis */}
            {renderControls()}

            {/* Conteúdo que muda baseado nos dados e estado de carregamento */}
            {categoryDataQuery.isLoading ? (
                <LoadingWrapper>
                    <CircularProgress />
                </LoadingWrapper>
            ) : categoryDataQuery.isError || !categoryData.length ? (
                <NoDataMessage>
                    {categoryDataQuery.isError
                        ? 'Erro ao carregar os dados.'
                        : 'Nenhuma informação encontrada para o período selecionado.'}
                </NoDataMessage>
            ) : (
                <Box sx={{ width: '100%', height: '100%' }}>
                    {currentCategoryData ? (
                        view === 'chart' ? (
                            <ChartsContainer>
                                {/* Gráfico de Barras */}
                                <ChartWrapper>
                                    <Chart
                                        chartType="BarChart"
                                        width="100%"
                                        height="100%"
                                        data={barChartData}
                                        options={{
                                            title: `Top ${currentCategoryData.productsForChart.length} Produtos Mais Vendidos`,
                                            titleTextStyle: {
                                                fontSize: 14,
                                                bold: true
                                            },
                                            hAxis: {
                                                title: 'Quantidade',
                                                minValue: 0
                                            },
                                            vAxis: {
                                                title: 'Produto',
                                                textStyle: {
                                                    fontSize: 8 // Fonte menor para os nomes dos produtos
                                                }
                                            },
                                            colors: [theme.COLORS.YELLOW2],
                                            bar: { groupWidth: '70%' },
                                            legend: { position: 'none' },
                                            animation: {
                                                startup: true,
                                                duration: 500,
                                                easing: 'out'
                                            },
                                            chartArea: {
                                                width: '60%',
                                                height: '80%'
                                            }
                                        }}
                                    />
                                </ChartWrapper>
                            </ChartsContainer>
                        ) : (
                            <>
                                <TableWrapper>
                                    <Table stickyHeader aria-label="produtos por categoria">
                                        <TableHead>
                                            <TableRow>
                                                <StyledTableHeaderCell width="5%">Posição</StyledTableHeaderCell>
                                                <StyledTableHeaderCell width="75%">Produto</StyledTableHeaderCell>
                                                <StyledTableHeaderCell width="20%" align="right">Valor unitário</StyledTableHeaderCell>
                                                <StyledTableHeaderCell width="20%" align="right">Total</StyledTableHeaderCell>
                                                <StyledTableHeaderCell width="20%" align="right">Quantidade</StyledTableHeaderCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {currentCategoryData.products
                                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                                .map((product, index) => (
                                                    <TableRow
                                                        key={index}
                                                        sx={{
                                                            '&:nth-of-type(odd)': {
                                                                backgroundColor: theme.COLORS.BACKGROUND
                                                            },
                                                            '&:hover': {
                                                                backgroundColor: `${theme.COLORS.YELLOW2}22`
                                                            }
                                                        }}
                                                    >
                                                        <TableCell align="center">
                                                            <PositionBadge position={page * rowsPerPage + index + 1}>
                                                                {page * rowsPerPage + index + 1}
                                                            </PositionBadge>
                                                        </TableCell>
                                                        <TableCell component="th" scope="row">
                                                            {formatProductName(product.productName)}
                                                        </TableCell>
                                                        <TableCell align="right">{formatCurrencyBR(product.priceUnitCost)}</TableCell>
                                                        <TableCell align="right">{formatCurrencyBR(product.totalAmount * product.priceUnitCost)}</TableCell>
                                                        <TableCell align="right">{product.totalAmount}</TableCell>
                                                    </TableRow>
                                                ))}
                                            {currentCategoryData.products.length === 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                                                        Nenhum produto encontrado com os filtros selecionados.
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableWrapper>
                                <Box sx={{ mt: 2, p: 2, bgcolor: theme.COLORS.BACKGROUND, borderRadius: '4px', display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                        Produtos encontrados: {currentCategoryData.totalFilteredCount}
                                    </Typography>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.COLORS.YELLOW2 }}>
                                        Total R$: {formatCurrencyBR(currentCategoryData.totalAmountSold)}
                                    </Typography>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.COLORS.YELLOW2 }}>
                                        Total vendido: {currentCategoryData.totalQuantitySold} unidades
                                    </Typography>
                                </Box>
                            </>
                        )
                    ) : (
                        <NoDataMessage>
                            Selecione uma categoria para visualizar os dados
                        </NoDataMessage>
                    )}
                </Box>
            )}
        </TableContainer>
    )
}

// Componentes estilizados
const StyledBox = styled(Box)`
    display: flex !important;
    justify-content: space-between !important;
    align-items: center !important;
    gap: 0.5rem !important;
  `

const StyledTypography = styled.div`
    color: ${theme.COLORS.YELLOW2} !important;
    font-weight: bold !important;
    font-size: 15px !important;
    text-align: center !important;
  `

const LoadingWrapper = styled.div`
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
    height: 300px !important;
  `

const NoDataMessage = styled.div`
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
    height: 300px !important;
    font-size: 15px !important;
    font-weight: bold !important;
    color: ${theme.COLORS.YELLOW2} !important;
  `

const ControlBox = styled(Box)`
    display: flex !important;
    justify-content: space-between !important;
    align-items: center !important;
    padding: 1rem 0 !important;
  `

const ActionButton = styled.button`
    background-color: ${theme.COLORS.YELLOW2} !important;
    color: white !important;
    border: none !important;
    border-radius: 4px !important;
    padding: 0.5rem 1rem !important;
    font-weight: bold !important;
    cursor: pointer !important;
    transition: all 0.2s !important;
    
    &:hover {
      opacity: 0.8 !important;
    }
    
    &:disabled {
      background-color: ${theme.COLORS.GRAY3} !important;
      cursor: not-allowed !important;
    }
    
    &.active {
      background-color: ${theme.COLORS.GRAY3} !important;
      box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.2) !important;
    }
  `

const ChartsContainer = styled(Box)`
    display: flex !important;
    flex-direction: row !important;
    gap: 1rem !important;
    height: 500px !important;
    
    @media (max-width: 1200px) {
      flex-direction: column !important;
      height: auto !important;
    }
  `

const ChartWrapper = styled(Box)`
    flex: 1 !important;
    min-height: 400px !important;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1) !important;
    border-radius: 4px !important;
    padding: 1rem !important;
    background-color: white !important;
    transition: all 0.3s ease !important;
    
    &:hover {
      box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.15) !important;
    }
  `

const TableWrapper = styled(Box)`
    height: 500px !important;
    overflow-y: auto !important;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1) !important;
    border-radius: 4px !important;
  `

const StyledTableHeaderCell = styled(TableCell)`
    background-color: ${theme.COLORS.YELLOW2} !important;
    color: white !important;
    font-weight: bold !important;
  `

const FilterBox = styled(Box)`
    display: flex !important;
    justify-content: space-between !important;
    align-items: center !important;
    padding: 1rem 0 !important;
    border-bottom: 1px solid ${theme.COLORS.GRAY1} !important;
    margin-bottom: 1rem !important;
  `

const DisplaySelector = styled(Box)`
    display: flex !important;
    align-items: center !important;
    gap: 0.5rem !important;
  `

const DisplayLabel = styled.span`
    font-weight: bold !important;
    color: ${theme.COLORS.GRAY3} !important;
  `

const PositionBadge = styled.div<{ position: number }>`
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    width: 28px !important;
    height: 28px !important;
    border-radius: 50% !important;
    background-color: ${props =>
        props.position <= 3
            ? theme.COLORS.YELLOW2
            : props.position <= 10
                ? '#7986CB'
                : theme.COLORS.GRAY2
    } !important;
    color: white !important;
    font-weight: bold !important;
    font-size: 12px !important;
  `